import { AlertTriangle, CheckCircle2, Info } from "lucide-react";

/**
 * Human-readable labels for each issue slug the AI can emit.
 * Kept as an English-first list; localization can layer on later.
 */
export const READABILITY_ISSUE_LABELS = {
  blurry_out_of_focus: "Image is out of focus / blurry",
  motion_blur: "Motion blur — camera or hand moved during the shot",
  low_resolution: "Low resolution — text hard to read at this size",
  heavy_compression_artifacts: "Heavy compression — image was over-compressed",
  poor_lighting: "Poor lighting",
  glare_or_reflection: "Glare or reflection on the surface",
  shadow_across_text: "Shadow falls across the text",
  corner_cut_off: "A corner is cut off",
  edge_cut_off: "An edge is cut off",
  skewed_or_rotated: "Document is skewed / not aligned straight",
  wrinkled_or_folded: "Paper is wrinkled or folded",
  bleed_through_from_back_side: "Ink from the back side is bleeding through the paper",
  obscured_by_hand_or_object: "Part of the document is covered by a hand or object",
  watermark_interference: "Watermark makes some text hard to read",
  faded_ink: "Ink is faded",
  water_damage_or_stain: "Water damage or a stain covers part of the text",
  redaction_or_marker_covering_text: "Something (marker / redaction) is covering text",
  text_too_small_to_read: "Text is too small to read at this zoom / resolution",
  black_and_white_of_color_original: "Black-and-white scan of a document that USCIS expects in color",
  handwritten_text_hard_to_read: "Handwritten text is hard to read",
  other: "Other quality issue",
  // Legacy slug from earlier prompt version — keep as fallback:
  blurry: "Image is blurry",
};

/**
 * Per-issue coaching: one concrete, actionable fix the user can try.
 * Keep these plain-English, phone-friendly, no jargon.
 */
export const READABILITY_ISSUE_FIXES = {
  blurry_out_of_focus: "Tap the document on your screen so the camera focuses, then take the photo. Don't zoom in with pinch — get physically closer.",
  motion_blur: "Rest your elbows on the table or use both hands to hold the phone. Wait until the preview is sharp before pressing the button.",
  low_resolution: "Use your phone's main camera at full resolution — not a screenshot of a preview. If it's a PDF, re-export it at higher quality.",
  heavy_compression_artifacts: "Send yourself the original file (not a chat / social-media re-download, which crushes quality). Upload the original.",
  poor_lighting: "Move next to a window or turn on a desk lamp. Aim for even, bright light on the whole page.",
  glare_or_reflection: "Angle the camera slightly so light doesn't bounce back into the lens. Move any glossy surfaces (glass, plastic sleeves) out of the frame.",
  shadow_across_text: "Move your hand / body / phone so the shadow falls off the page — you want the whole document evenly lit.",
  corner_cut_off: "Pull the camera back a few inches until all four corners are visible in the frame, then re-take.",
  edge_cut_off: "Re-take with the whole page in view — center the document and leave a little margin on every side.",
  skewed_or_rotated: "Line up the phone so it's parallel with the document. Most cameras have a grid overlay that helps.",
  wrinkled_or_folded: "Flatten the paper — press it under a book for a minute, then scan. If a fold is unavoidable, take multiple photos so the fold isn't over any text.",
  bleed_through_from_back_side: "Put a dark sheet of paper (or a folder) directly behind the document before you scan. That blocks the back-page ink from showing through.",
  obscured_by_hand_or_object: "Move your fingers / thumb off the paper. Use a weight (like a coin) to hold corners down instead of your hand.",
  watermark_interference: "Try a scanner app (e.g. Adobe Scan or Apple Notes → Scan Document) — they enhance contrast and often make watermarks less overwhelming.",
  faded_ink: "Increase contrast in your scanner app before uploading, or hold the document toward a bright light so the letters darken visibly in preview.",
  water_damage_or_stain: "If you have another copy of this document, use it. Otherwise, USCIS accepts a certified replacement — contact the issuing office for a new one.",
  redaction_or_marker_covering_text: "USCIS needs the unredacted document. If you redacted it yourself, upload the original. If the copy was already redacted, contact the issuer for a clean one.",
  text_too_small_to_read: "Get physically closer to the document (about 6-8 inches away) so the text fills more of the frame. Don't use digital zoom.",
  black_and_white_of_color_original: "USCIS often needs color scans (especially for photos on IDs). Re-scan in color — most phone camera apps default to color already.",
  handwritten_text_hard_to_read: "Type a transcription of the handwriting in the notes field, or ask the original writer for a clean typed copy.",
  other: "Re-take the scan with better light, a flat surface, and the whole document in the frame. If issues persist, use a scanner app like Adobe Scan.",
  blurry: "Tap the document on your screen so the camera focuses, then take the photo.",
};

/**
 * Convert an issue slug list into a comma-joined plain-English string.
 */
export function humanizeReadabilityIssues(issues = []) {
  if (!issues || issues.length === 0) return "";
  return issues.map((s) => READABILITY_ISSUE_LABELS[s] || s.replace(/_/g, " ")).join(" · ");
}

/**
 * Compact readability badge shown next to each uploaded doc.
 * Reads from `extracted.readability` and gracefully renders nothing if absent
 * (so older docs uploaded before this feature don't show a broken pill).
 */
export default function ReadabilityBadge({ readability, compact = false }) {
  if (!readability || typeof readability !== "object") return null;
  const scoreRaw = readability.score;
  const score = Number.isFinite(scoreRaw) ? Math.round(scoreRaw) : null;
  if (score === null) return null;

  const tier =
    score >= 85 ? { grade: "Excellent", color: "#4B6A4F", bg: "#EEF3EA", Icon: CheckCircle2 }
    : score >= 60 ? { grade: "Readable — could be sharper", color: "#8A6B4F", bg: "#FDF8F0", Icon: Info }
    : { grade: "Hard to read — please try a clearer copy", color: "#B04A1A", bg: "#FBEAE0", Icon: AlertTriangle };

  const issues = humanizeReadabilityIssues(readability.issues || []);
  const summary = readability.issues_summary || issues;

  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
        style={{ background: tier.bg, color: tier.color }}
        title={summary || tier.grade}
        data-testid="readability-badge-compact"
      >
        <tier.Icon size={11} /> Quality {score}/100
      </span>
    );
  }

  return (
    <div
      className="mt-2 rounded-lg p-3 text-xs"
      style={{ background: tier.bg, color: tier.color }}
      data-testid="readability-badge"
    >
      <div className="flex items-start gap-2">
        <tier.Icon size={14} className="mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-semibold">
            Quality {score}/100 — {tier.grade}
          </div>
          {summary && <div className="mt-0.5 opacity-90">{summary}</div>}
          {readability.recommend_reupload && (
            <div className="mt-1 font-medium">
              Recommendation: upload a better copy so USCIS can read this document.
            </div>
          )}
        </div>
      </div>
      {(readability.issues || []).length > 0 && score < 85 && (
        <ReadabilityCoaching issues={readability.issues} />
      )}
    </div>
  );
}

function ReadabilityCoaching({ issues }) {
  // Deduplicate + cap at 4 tips so we don't overwhelm.
  const unique = [...new Set(issues || [])].slice(0, 4);
  if (!unique.length) return null;
  return (
    <details className="mt-2 pt-2 border-t" style={{ borderColor: "rgba(0,0,0,0.1)" }} data-testid="readability-coaching">
      <summary className="cursor-pointer text-[11px] font-semibold select-none" style={{ opacity: 0.85 }}>
        How to fix these issues ({unique.length})
      </summary>
      <ul className="mt-2 space-y-1.5">
        {unique.map((slug) => {
          const label = READABILITY_ISSUE_LABELS[slug] || slug.replace(/_/g, " ");
          const tip = READABILITY_ISSUE_FIXES[slug] || READABILITY_ISSUE_FIXES.other;
          return (
            <li key={slug} className="text-[11px] leading-snug" data-testid={`coaching-${slug}`}>
              <span className="font-semibold">{label}:</span>{" "}
              <span className="opacity-90">{tip}</span>
            </li>
          );
        })}
      </ul>
    </details>
  );
}

/**
 * A small tip block to show above upload dropzones.
 * Reusable — one tone, no localization deps.
 */
export function ScanQualityTip({ variant = "default" }) {
  const compact = variant === "compact";
  return (
    <div
      className={`rounded-lg border-l-3 ${compact ? "p-2" : "p-3"} text-xs flex items-start gap-2`}
      style={{ background: "#FDF8F0", borderColor: "#C85A32", color: "#8A6B4F" }}
      data-testid="scan-quality-tip"
    >
      <Info size={14} className="mt-0.5 shrink-0" color="#C85A32" />
      <div>
        <strong style={{ color: "#2F2A24" }}>For USCIS-ready scans:</strong> use bright, even lighting; lay
        the document flat; keep all four corners in the frame; avoid glare and shadows; hold the camera steady;
        make sure text is sharp. If the paper is thin, put a dark sheet behind it so ink from the back doesn't
        bleed through. Type or transcribe hard-to-read handwriting when possible. We'll rate each scan and flag
        anything that might trigger a Request for Evidence.
      </div>
    </div>
  );
}
